<template>
  <div id="payment" class="px-5 pt-3">
    <v-layout row wrap>
      <v-flex xs12 mb-3>
        <h5 class="pt-2">{{i18n('Forma de Pagamento')}}</h5>
        <v-select dark class="black-select" v-model="payment_method"
          :items="paymentMethodItems"
          required
          data-testid="new-transaction-payment-method-select"
        ></v-select>
        <v-layout v-if="payment_method == 'ted'" row wrap mt-2>
          <v-flex xs12>
            <h5 class="pt-2">{{i18n('Conta para TED')}}</h5>
            <v-autocomplete class="black-autocomplete"
              dark
              :placeholder="i18n('Pesquise pelo número da conta, digite pelo menos 3 caracteres e tecle enter')"
              v-model="from_bank_account"
              :items="tedBankAccountItems"
              :no-data-text="noDataText"
              @click:clear="clearAutocomplete"
              v-on:keyup.enter="findBankAccountByNumber($event)"
            ></v-autocomplete>
            <h5 class="pt-2">{{i18n('Informe o valor do TED')}}</h5>
            <v-currency-field
              :label="`${i18n('Valor')} (${fromCurrency})`"
              outline
              dark
              class="input-brl-value"
              v-model="value"
              decimal=","
              thousands="."
              :precision="fromValuePrecision"
            ></v-currency-field>
          </v-flex>
        </v-layout>
        <v-layout v-if="payment_method == 'bank_account_balance'" row wrap>
          <v-flex xs12>
            <h5 class="pt-2">{{i18n('Conta para débito')}}</h5>
            <v-select dark class="black-select" v-model="from_internal_bank_account"
              :items="getValidFromDebitAccounts(partnerInternalBankAccountFromItems)"
              required
              data-testid="new-transaction-debit-account-select"
            ></v-select>

            <!-- To Bank Account Select when partner has multiple internal accounts (e.g. facilita_cash_out) -->
            <div v-if="['exchange_internal', 'fee_payoff'].includes(context.selected_operation) && partnerHasMoreThanOneInternalBankAccount()">
              <h5 class="pt-2">{{i18n('Conta destino')}}</h5>
              <v-select dark class="black-select" v-model="to_bank_account"
                :items="partnerInternalToBankAccountListForExchange()"
                required
                data-testid="new-transaction-to-account-select"
              ></v-select>
            </div>

            <h5 class="pt-2">{{i18n('Informe o Valor')}}</h5>
            <div class="m-0 container-input-value" v-if="reverseExchangeAllowed">
              <div class="m-0 p-0" style="margin-top: -6px; padding-bottom: 4px;">
                <v-checkbox
                  v-model="reverse_exchange"
                  hide-details
                  dark
                  :label="i18n('Definir o valor final')"
                  data-testid="new-transaction-set-value-checkbox">
                </v-checkbox>
              </div>
              <div style="display: flex;">
                <v-currency-field
                :label="`${i18n('Valor')} (${fromCurrency})`"
                outline
                dark
                v-if="!reverse_exchange"
                class="input-brl-value"
                v-model="value"
                decimal=","
                thousands="."
                :precision="fromValuePrecision"
                ></v-currency-field>
                <v-currency-field
                  :label="`${i18n('Valor')} (${toCurrency})`"
                  outline
                  dark
                  v-if="reverse_exchange"
                  class="input-brl-value"
                  v-model="exchanged_value"
                  decimal=","
                  thousands="."
                  :precision="toValuePrecision"
                ></v-currency-field>
              </div>
            </div>
            <div style="display: flex;" v-else>
              <v-currency-field
              :label="`${i18n('Valor')} (${fromCurrency})`"
              outline
              dark
              v-if="!reverse_exchange"
              class="input-brl-value"
              v-model="value"
              decimal=","
              thousands="."
              :precision="fromValuePrecision"
              ></v-currency-field>
            </div>
          </v-flex>
        </v-layout>
        <v-layout v-if="payment_method == 'wire'" row wrap mt-2>
          <v-flex xs12>

            <h5 class="pt-2">{{i18n('Conta para Wire')}}</h5>
            <v-autocomplete class="black-autocomplete"
              dark
              :placeholder="i18n('Pesquise pelo número da conta, digite pelo menos 3 caracteres e tecle enter')"
              v-model="from_bank_account"
              :items="tedBankAccountItems"
              :no-data-text="noDataText"
              @click:clear="clearAutocomplete"
              data-testid="new-transaction-wire-account-select"
              v-on:keyup.enter="findBankAccountByNumber($event)"
            ></v-autocomplete>

            <!-- To Bank Account Select when partner has multiple internal accounts (e.g. facilita_cash_out) -->
            <div v-if="context.selected_operation == 'deposit' && partnerHasMoreThanOneInternalBankAccount()">
              <h5 class="pt-2">{{i18n('Conta destino')}}</h5>
              <v-select dark class="black-select" v-model="to_bank_account"
                :items="partnerInternalToBankAccountListForDeposit()"
                data-testid="new-transaction-destination-account-select"
                required
              ></v-select>
            </div>

            <h5 class="pt-2">{{i18n('Informe o valor do Wire')}}</h5>
            <div style="display: flex;">
              <v-currency-field
              :label="`${i18n('Valor')} (${fromCurrency})`"
              outline
              dark
              v-if="!reverse_exchange"
              class="input-brl-value"
              v-model="value"
              decimal=","
              thousands="."
              :precision="fromValuePrecision"
              data-testid="new-transaction-wire-value-input"
              ></v-currency-field>
            </div>
          </v-flex>
        </v-layout>
        <v-layout v-if="forExchange" row wrap>
          <v-flex xs12>
            <h5 class="pt-2">{{i18n('Realizar conversão')}}</h5>
            <v-select dark class="black-select" v-model="exchangeUnderRequest"
              :items="exchangeUnderRequestOptions"
              data-testid="new-transaction-made-conversion-select"
              required
            ></v-select>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>
    <div class="buttons-container">
      <v-btn class="default-button" color="#BCC1D1" dark @click="$emit('handle-event', {back: true})" data-testid="new-transaction-rollback-button">
        {{i18n('Voltar')}}
      </v-btn>
      <v-btn class="default-button" color="#27DABC" :disabled="!enableNextButton" dark @click="goForward()" data-testid="new-transaction-continue-button">
        <div class="icon-confirm">
          {{i18n('Prosseguir')}}
          <ion-icon name="arrow-forward" style="padding: 10px;"></ion-icon>
        </div>
      </v-btn>
    </div>
  </div>
</template>

<script>
import utils from '../../shared/Utils'
import {BankAccountsApi} from '@/shared/BankAccountsApi'

export default {
  name: 'PaymentView',
  data() {
    return {
      noDataText: '',
      from_bank_account: null,
      to_bank_account: null,
      from_internal_bank_account: null,
      to_internal_bank_account: null,
      payment_method: 'ted',
      value: 0.0,
      exchanged_value: 0.0,
      fxBankAccounts: [],
      partnerBankAccounts: [],
      partnerInternalBankAccountFromItems: [],
      partnerInternalBankAccountToItems: [],
      company: null,
      exchangeUnderRequest: false,
      permission: {},
      reverse_exchange: false,
      transaction_state: {},
    }
  },
  props: {
    context: {
      type: Object
    }
  },
  methods: {
    i18n(str) {
      return this.$root.l(str)
    },
    localeCurrency(value, currency) {
      return this.$root.localeCurrency(value, currency)
    },
    goForward() {
      let flow_type = this.context.operation_flow
      if (this.payment_method == 'bank_account_balance') {
        this.from_bank_account = this.from_internal_bank_account
        if (this.context.selected_operation == 'exchange_internal') {
          flow_type = !this.from_internal_bank_account.flow_type ? 'both' : this.from_internal_bank_account.flow_type
        }
      }

      if (this.reverse_exchange) {
        this.value = 0.0
      } else {
        this.exchanged_value = 0.0
      }

      let payload = {createTransaction: true, value: this.value, reverse_exchange: this.reverse_exchange, exchanged_value: this.exchanged_value, payment_method: this.payment_method, from_bank_account: this.from_bank_account, fromCurrency: this.fromCurrency, toCurrency: this.toCurrency, exchange_under_request: this.exchangeUnderRequest}

      if (['deposit', 'withdrawal_internal', 'exchange_internal', 'fee_payoff'].includes(this.context.selected_operation)) {
        payload.selected_subject = this.company
      }

      // selected_bank_account (to_bank_account) and selected_from_bank_account (from_bank_account)
      // should not be determined automatically anymore for 'deposit' and 'exchange_internal' flows
      // when there are multiple internal accounts (e.g. facilita_cash_out)
      if (['deposit', 'exchange_internal', 'fee_payoff'].includes(this.context.selected_operation)) {
        if (this.partnerHasMoreThanOneInternalBankAccount()){
          payload.selected_bank_account = this.to_bank_account
        } else {
          payload.selected_bank_account = this.internalBankAccount(this.toCurrency, flow_type)
        }
      }

      this.$emit('handle-event', payload)
    },
    internalBankAccount(currency, flow_type = 'both') {
      let bank_accounts = this.partnerBankAccounts.filter( ( record ) => {
        if (flow_type == 'both') {
          return record.internal == true && record.currency == currency && !record.flow_type
        } else {
          return record.internal == true && record.currency == currency && record.flow_type == flow_type
        }
      } )
      if (bank_accounts.length > 0) {
        return bank_accounts[0]
      } else {
        if (flow_type != 'both') {
          bank_accounts = this.partnerBankAccounts.filter( ( record ) => { return record.internal == true && record.currency == currency && !record.flow_type } )
          if (bank_accounts.length > 0) {
            return bank_accounts[0]
          }
        }
      }
      return false
    },
    loadPartnerBankAccounts() {
      let successCallback = (response) => {
        this.loadedPartnerBankAccountStatus = 'ok'
        if (response.body.data) {
          this.partnerBankAccounts = response.body.data
          this.loadBalances()
        }
      }

      let errorCallback = (response) => {
        if (response.status === 401) {
          this.loadedPartnerBankAccountStatus = 'not_authorized'
        } else {
          this.loadedPartnerBankAccountStatus = 'error'
        }
        console.log('errorCallback', response)
      }

      let token = this.$cookies.get('api_token')
      this.$http.get(this.$root.urls.bank_accounts, { headers: { Authorization: `Bearer ${token}` }}).then(successCallback, errorCallback)
    },
    getValidFromDebitAccounts(partnerInternalBankAccountFromItems) {
      // facilita_cash_out is not a valid "debit" account, you want to send money to this account in order to pay fees
      // but you shouldn't be able to take money from it
      return partnerInternalBankAccountFromItems.filter(acc => acc.value.flow_type != 'facilita_cash_out')
    },
    isEligibleForDestinationBankAccount(bank_account, context, operation_flow) {
      return ((bank_account.currency == context.component.toCurrency) && (
        (operation_flow == 'both' && bank_account.flow_type != 'facilita_cash_out') || (bank_account.flow_type == operation_flow && ['cash_out', 'facilita_cash_out'].includes(operation_flow)))
      )
    },
    partnerHasMoreThanOneInternalBankAccount() {
      return this.partnerBankAccounts.filter(acc => acc.internal).length > 0
    },
    partnerInternalToBankAccountListForExchange() {
      if (this.from_internal_bank_account == null || this.from_internal_bank_account == "facilita_cash_out") {
        return []
      }

      return this.partnerInternalBankAccountToItems.filter(acc => {
        return acc.value.internal &&
          acc.value.flow_type == null ||
          this.from_internal_bank_account.flow_type == null ||
          this.from_internal_bank_account.flow_type == acc.value.flow_type ||
          (this.from_internal_bank_account.flow_type == "cash_out" && acc.value.flow_type == "facilita_cash_out");
        }
      )
    },
    partnerInternalToBankAccountListForDeposit() {
      return this.partnerBankAccounts
        .filter(acc =>
          acc.internal &&
          acc.currency == this.transaction_state.toCurrency &&
          (acc.flow_type == null || acc.flow_type == "cash_out")
        )
        .map(acc => {
          return {
            text: utils.longAccountNameForInternalWithoutPrefix(acc),
            value: acc
          }
        })
    },
    loadBalances() {
      this.partnerInternalBankAccountFromItems = []
      this.partnerInternalBankAccountToItems = []

      const token = this.$cookies.get('api_token')
      const partnerBankAccounts = this.partnerBankAccounts
      const component = this
      const operation_flow = this.context.operation_flow
      const selected_operation = this.context.selected_operation

      let successCallbackFor = (context) => {
        return function(response) {
          if (response.body.data) {
            const index = context.partnerBankAccounts.findIndex(el => el.id === context.id)
            let bank_account = context.partnerBankAccounts[index]
            bank_account.balance = response.body.data.balance
            const internal_account_name = utils.longAccountNameForInternalWithoutPrefix(bank_account)
            if (bank_account.currency == context.component.fromCurrency) {
              context.component.partnerInternalBankAccountFromItems.push({text: `${internal_account_name} (${context.component.localeCurrency((bank_account.balance || 0.0), bank_account.currency)})`, value: bank_account})
            }
            if (this.isEligibleForDestinationBankAccount(bank_account, context, operation_flow)) {
              context.component.partnerInternalBankAccountToItems.push({text: `${internal_account_name} (${context.component.localeCurrency((bank_account.balance || 0.0), bank_account.currency)})`, value: bank_account})
            }
            this.transaction_state = {fromCurrency: context.component.fromCurrency, toCurrency: context.component.toCurrency}
          } else {
            console.log(response)
          }
        }
      }

      let errorCallbackFor = (context) => {
        return function(response) {
          console.log(response)
        }
      }

      this.partnerBankAccounts.filter(record => { return !!record.internal && (
        !record.flow_type ||
        operation_flow == 'both' ||
        record.flow_type == operation_flow ||
        (record.flow_type == 'cash_out' && selected_operation == 'fee_payoff')
        )}).forEach(record => {
        const url = `${this.$root.urls.bank_accounts}${record.id}/balance`
        const id = record.id
        const context = {id, partnerBankAccounts, component}
        this.$http.get(url, { headers: { Authorization: `Bearer ${token}` }}).then(successCallbackFor(context), errorCallbackFor(context))
      })
    },
    loadBankAccounts() {
      let successCallback = (response) => {
        this.fxBankAccounts = response.body.data
      }

      let errorCallback = (response) => {
        if (response.status === 401) {
          this.companyLoaded = 'not_authorized'
        } else {
          this.companyLoaded = 'error'
        }
        console.log('errorCallback', response)
      }
      BankAccountsApi.onlyGet(this, this.$root.urls.fx_bank_accounts, successCallback, errorCallback, { per_page: 20 });
    },
    findBankAccountByNumber(event) {
      let successCallback = (response) => {
        this.fxBankAccounts = response.body.data
      }

      let errorCallback = (response) => {
        if (response.status === 401) {
          this.companyLoaded = 'not_authorized'
        } else {
          this.companyLoaded = 'error'
        }
        console.log('errorCallback', response)
      }

      BankAccountsApi.loadBankAccountByNumberOrNickname(this, this.$root.urls.fx_bank_accounts, successCallback, errorCallback, event.srcElement.value);
    },
    setDefaultNoTextLabel() {
      this.noDataText = this.i18n('Digite pelo menos 3 caracteres e tecle enter')
    },
    clearAutocomplete(){
      this.fxBankAccounts = [];
      this.setDefaultNoTextLabel()
    },
    i18n(str) {
      return this.$root.l(str)
    },
  },
  computed: {
    reverseExchangeAllowed() {
      return this.payment_method == 'bank_account_balance' && this.fromCurrency != this.toCurrency
    },
    fromCurrency() {
      return this.context.selected_from_currency
    },
    toCurrency() {
      return this.context.selected_to_currency
    },
    forExchange() {
      return this.fromCurrency != this.toCurrency
    },
    tedBankAccountItems() {
      let exclusive_bank_acounts = this.fxBankAccounts.filter( ( record ) => { return !!record.exclusive_for && record.currency == this.fromCurrency } )

      if (exclusive_bank_acounts.length > 0) {
        return exclusive_bank_acounts.map(x => { return {text: utils.longAccountName(x), value: x} })
      }
      let shared_bank_acounts = this.fxBankAccounts.filter( ( record ) => { return !record.exclusive_for && record.currency == this.fromCurrency } )
      if (shared_bank_acounts.length > 0) {
        return shared_bank_acounts.map(x => { return {text: utils.longAccountName(x), value: x} })
      }
      this.noDataText = this.i18n('Nenhum registro encontrado')
      return [];
    },
    fromValuePrecision() {
      return this.fromCurrency == 'CLP' ? 0 : 2
    },
    toValuePrecision() {
      return this.toCurrency == 'CLP' ? 0 : 2
    },
    exchangeUnderRequestOptions() {
      if (this.company === null || this.company.partners_config === null)
        return;

      const userFacilitaExchangeOnlyPermission = this.$root.userAllowedTo('facilita_exchange_only')
      const userCustomerExchangeOnlyPermission = this.$root.userAllowedTo('customer_exchange_only')

      const onlyAutoFxOption = { text: this.i18n('FX automático'), value: true }
      const onlyFxOnDemandOption = { text: this.i18n('FX sob demanda'), value: false }
      const partnerTriggerRule = this.company.partners_config.exchange_mode

      let options = []

      switch (partnerTriggerRule) {
        case 'auto_fx':
          options = [onlyAutoFxOption]
          break;
        case 'fx_on_demand':
          options = [onlyFxOnDemandOption]
          break;
        case 'any':
          if (userCustomerExchangeOnlyPermission)
            options = [onlyAutoFxOption]
          else if (userFacilitaExchangeOnlyPermission)
            options = [onlyFxOnDemandOption]
          else
            options = [onlyAutoFxOption, onlyFxOnDemandOption]

          break;
        default:
          throw new Error(`Unknown value for exchange trigger: ${partnerTriggerRule}`)
      }

      return options;
    },
    exchangeUnderRequestSingleOption() {
      const options = this.exchangeUnderRequestOptions
      
      return options && options.length === 1
        ? options[0].value
        : undefined
    },
    paymentMethodItems() {
      let result = []
      const ted = {text: this.i18n('TED para conta FacilitaPay'), value: 'ted'}
      const bank_account_balance = {text: this.i18n('Saldo conta interna'), value: 'bank_account_balance'}
      const wire = {text: this.i18n('Wire para conta FacilitaPay'), value: 'wire'}

      switch(this.context.selected_operation) {
        case 'receive':
          result.push(ted)
          break
        case 'pay':
          if (this.fromCurrency != 'BRL') result.push(wire)
          result.push(bank_account_balance)
          break
        case 'deposit':
          result.push(wire)
          break
        case 'withdrawal_internal':
        case 'exchange_internal':
        case 'fee_payoff':
          result.push(bank_account_balance)
          break
        default:
          break
      }

      return result
    },
    enableNextButton() {
      if ((this.reverse_exchange == false && this.value <= 0) || (this.reverse_exchange == true && this.exchanged_value <= 0)) {
        return false
      }

      if (this.context.selected_operation == "receive") {
        return this.context.selected_bank_account != null && this.from_bank_account != null
      }

      if (this.context.selected_operation == "pay") {
        return this.context.selected_bank_account != null && ((this.payment_method == "wire" && this.from_bank_account != null) || (this.payment_method == "bank_account_balance" && this.from_internal_bank_account != null))
      }

      if (this.context.selected_operation == "deposit") {
        return  this.from_bank_account != null && this.to_bank_account != null
      }

      if (this.context.selected_operation == "withdrawal_internal") {
        return this.context.selected_bank_account != null && this.from_internal_bank_account != null
      }

      if (this.context.selected_operation == "exchange_internal") {
        return this.from_internal_bank_account != null && this.to_bank_account != null
      }

      if (this.context.selected_operation == "fee_payoff") {
        return this.from_internal_bank_account != null && this.to_bank_account != null
      }

      return false
    }
  },
  watch: {
    exchangeUnderRequestSingleOption(newValue) {
      if (!this.exchangeUnderRequest) 
        this.exchangeUnderRequest = newValue      
    }
  },
  mounted() {
    this.setDefaultNoTextLabel()
    this.loadPartnerBankAccounts()
    this.loadBankAccounts()
    this.payment_method = this.context.selected_payment_method || this.paymentMethodItems[0]['value']
    this.from_bank_account = this.context.selected_from_bank_account
    this.value = this.context.selected_value
    this.exchanged_value = this.context.selected_exchanged_value
    this.reverse_exchange = this.context.reverse_exchange

    this.$root.store.getCompany((company) => {
      if (company)
        this.company = company
    })
  }
}
</script>

<style>
#payment .input-brl {
  height: 56px;
  background-color: #0C2E4A;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  border: 1px solid rgba(142, 174, 198, 0.2);
}
#payment .input-brl-value {
  height: 56px;
  background-color: #0C2E4A;
  border-radius: 4px;
}

#payment .input-brl-value .v-input__slot{
  border: 1px solid rgba(142, 174, 198, 0.2);
}

.black-select, .white-select {
  padding: 4px 8px 8px 8px!important;
  margin-bottom: 0px!important;
  margin-top: 0px!important;
}

.container-input-value .v-input--selection-controls {
  margin-top: 0px!important;
}
</style>



// WEBPACK FOOTER //
// src/components/NewTransaction/PaymentView.vue